import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import ProductCard from './ProductCard';
import { api } from '../lib/api';

interface RelatedProductsProps {
  skus: string[];
  /** Title shown above the grid (default "You may also like"). */
  title?: string;
}

/**
 * Renders the related-products grid on the product detail page. Reuses
 * the cached `['products']` query so it does not refetch — it just filters
 * the catalogue to the configured SKUs and preserves their order.
 */
export default function RelatedProducts({ skus, title = 'You may also like' }: RelatedProductsProps) {
  const { data: products = [] } = useQuery({
    queryKey: ['products'],
    queryFn: api.listProducts,
  });

  const items = useMemo(() => {
    if (!skus || skus.length === 0) return [];
    const bySku = new Map(products.map((p) => [p.rowKey, p]));
    return skus.map((s) => bySku.get(s)).filter((p): p is NonNullable<typeof p> => !!p && p.active);
  }, [products, skus]);

  if (items.length === 0) return null;

  return (
    <section className="bg-slate-50 py-14">
      <div className="container-page">
        <h2 className="mb-6 font-display text-2xl font-bold text-slate-900">{title}</h2>
        <div className="grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-4">
          {items.map((p) => (
            <ProductCard key={`${p.partitionKey}-${p.rowKey}`} product={p} />
          ))}
        </div>
      </div>
    </section>
  );
}
